import { Navigate, Route, Routes, useLocation } from "react-router";
import React, { Suspense, useEffect, useMemo } from "react";
import AuthLayout from "../../pages/auth/layout";
import AppLayout from "../../pages/main/layout";
const ForgotPasswordLayout = React.lazy(() =>
  import("../../pages/auth/forgotPassword")
);
const SignInLayout = React.lazy(() => import("../../pages/auth/signin"));
const SignUpLayout = React.lazy(() => import("../../pages/auth/signUp"));
const ResetPasswordLayout = React.lazy(() =>
  import("../../pages/auth/resetPassword")
);
const VerifyEmailLayout = React.lazy(() =>
  import("../../pages/auth/verifyEmail")
);
import useUserStore from "../../store/userStore";
import useAppStore from "../../store/appStore";
import { shallow } from "zustand/shallow";
const Dashboard = React.lazy(() => import("../../pages/main/dashboard"));
import { HiHome } from "react-icons/hi2";
const UserProfile = React.lazy(() => import("../../pages/main/userProfile"));
const PostPage = React.lazy(() => import("../../pages/main/content/post"));
const Test_page = React.lazy(() => import("../../pages/main/test_page"));
const PlaylistPage = React.lazy(() => import("../../pages/main/playlist"));
// import NewPostPage from "../../pages/main/create/post";
const NewPostPage = React.lazy(() => import("../../pages/main/create/post"));
const NewProductPage = React.lazy(() =>
  import("../../pages/main/create/product")
);
const MyPurchases = React.lazy(() => import("../../pages/main/myPurchases"));
import { RiPlayListLine } from "react-icons/ri";
import RouteManagerContext from "./RouteManagerContext";
import { MdExplore } from "react-icons/md";
import { BsCart3 } from "react-icons/bs";
const ProductPage = React.lazy(() =>
  import("../../pages/main/content/product")
);
const StorePage = React.lazy(() => import("../../pages/main/store"));

const AuthRoutes = [
  {
    path: "/",
    Component: SignUpLayout,
  },
  {
    path: "/sign-in",
    Component: SignInLayout,
  },
  {
    path: "/forgot",
    Component: ForgotPasswordLayout,
  },
  {
    path: "/reset-password",
    Component: ResetPasswordLayout,
  },
  {
    path: "/verify",
    Component: VerifyEmailLayout,
  },
];

/**
 * @type {Route[]}
 */
const AppRoutes = [
  {
    path: "/",
    index: true,
    icon: <MdExplore />,
    title: "Explore",
    Component: Dashboard,
    description: "The most exciting music at your fingertips",
    isNativeRoute: true,
  },
  {
    path: "/user/:username",
    icon: <HiHome />,
    title: "Profile",
    Component: UserProfile,
    ignoreRendering: true,
    description: "The most exciting music at your fingertips",
    isNativeRoute: true,
  },
  {
    path: "/user/:username/post/:contentId",
    title: "Content",
    Component: PostPage,
    ignoreRendering: true,
    description: "The most exciting music at your fingertips",
    isNativeRoute: true,
  },
  {
    path: "/user/:username/product/:contentId",
    title: "Product",
    Component: ProductPage,
    ignoreRendering: true,
    description: "The most exciting music at your fingertips",
    isNativeRoute: true,
  },
  {
    path: "/user/:username/store",
    title: "Store",
    Component: StorePage,
    ignoreRendering: true,
    isNativeRoute: true,
  },
  {
    path: "/user/:username/playlist/:playlistId",
    // icon: <HiHome />,
    // title: "Content",
    Component: PlaylistPage,
    ignoreRendering: true,
    isNativeRoute: true,
    icon: <RiPlayListLine />,
  },
  // {
  //   path: "/room",
  //   // icon: <HiHome />,
  //   // title: "Content",
  //   Component: GrooveRoomPage,
  //   ignoreRendering: true,
  //   isNativeRoute: true,
  // },
  // {
  //   path: "/room/:roomId",
  //   // icon: <HiHome />,
  //   // title: "Content",
  //   Component: GrooveRoomPage,
  //   ignoreRendering: true,
  //   isNativeRoute: true,
  // },
  {
    path: "/create",
    Component: <Navigate to={"/create/post"} />,
    ignoreRendering: true,
    isNativeRoute: true,
  },
  {
    path: "/create/post",
    Component: NewPostPage,
    ignoreRendering: true,
    isNativeRoute: true,
  },
  {
    path: "/create/product",
    Component: NewProductPage,
    ignoreRendering: true,
    isNativeRoute: true,
  },
  {
    path: "/orders-purchases",
    Component: MyPurchases,
    title: "My Orders",
    icon: <BsCart3 />,
    // ignoreRendering: true,
    isNativeRoute: true,
  },
  {
    path: "/test",
    Component: Test_page,
    title: "Test",
    isNativeRoute: true,
    ignoreRendering: true,
  },
];

const NotFoundRoute = () => {
  const user = useUserStore((state) => state.user, shallow) || {};
  const { id: stateUserId } = user;
  const setLastRoute = useAppStore((state) => state.setLastRoute);
  const lastRoute = useAppStore((state) => state.lastRoute);
  const location = useLocation();

  useEffect(() => {
    console.log("Got to not found", location.pathname);
    if (!stateUserId) {
      setLastRoute(location.pathname);
    } else if (lastRoute) {
      setLastRoute(null);
    }
  }, [stateUserId, lastRoute, location.pathname, setLastRoute]);

  return <Navigate to="/" replace />;
};

/**
 * RouteManager Provider Component
 * Provides route management functionality to child components
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @returns {React.ReactElement} Provider component
 */
export const RouteManagerProvider = ({ children }) => {
  const location = useLocation();
  const user = useUserStore((state) => state.user, shallow) || {};
  const { id: stateUserId } = user;

  /**
   * @typedef {Route & { modify: (options: Object) => Route }} ExtendedRoute
   */

  /** @type {[ExtendedRoute|null, function((ExtendedRoute|null) => (ExtendedRoute|null))]} */

  /**
   * Excludes routes from an array based on path matching
   * @param {Array<Route>} routes - Array of routes to filter
   * @param {string|Array<string>} toExclude - Path(s) to exclude
   * @returns {Array<Route>} Filtered array of routes
   */
  const excludeRoutes = (routes, toExclude) => {
    if (Array.isArray(routes)) {
      if (Array.isArray(toExclude)) {
        return routes.filter(
          ({ path }) => !toExclude.some((exlRoute) => exlRoute === path)
        );
      } else if (toExclude?.trim()?.length > 0) {
        return routes.filter(({ path }) => path !== toExclude);
      }

      return routes;
    }
  };

  const flattenRoutes = (routes = []) =>
    routes.flatMap(({ nestedRoutes, ...route }) =>
      [route].concat(nestedRoutes || [])
    );

  const allowedRoutes = useMemo(() => {
    if (stateUserId) {
      return {
        type: "app",
        routes: flattenRoutes(AppRoutes),
      };
    } else {
      return {
        type: "auth",
        routes: flattenRoutes(AuthRoutes),
      };
    }
  }, [stateUserId]);

  function updatePageMeta(primaryTitle, secondaryTitle, promoText) {
    if (primaryTitle && secondaryTitle) {
      document.title = `${primaryTitle} - ${secondaryTitle}, ${
        promoText || "exclusively on Tarara.app"
      }`;
    }
  }

  useEffect(() => {
    document.title = "Tarara.app - Connecting artists with fans";
  }, [location.pathname]);

  const value = useMemo(
    () => ({
      excludeRoutes,
      AuthRoutes,
      AppRoutes: allowedRoutes.routes,
      NotFoundRoute,
      updatePageMeta,
    }),
    [allowedRoutes.routes, stateUserId]
  );

  return (
    <RouteManagerContext.Provider value={value}>
      <Routes location={location}>
        {allowedRoutes.type === "auth" && (
          <Route element={<AuthLayout />}>
            {allowedRoutes.routes.map((route) => {
              const Elem = route.Component;
              return (
                <Route
                  key={route.path}
                  index={route.index}
                  path={route.path}
                  element={
                    React.isValidElement(Elem) ? (
                      <Suspense>{Elem}</Suspense>
                    ) : (
                      <Suspense>
                        <Elem />
                      </Suspense>
                    )
                  }
                />
              );
            })}
          </Route>
        )}
        {allowedRoutes.type === "app" && (
          <Route element={<AppLayout />}>
            {allowedRoutes.routes.map((route) => {
              if (route.isNativeRoute) {
                const Elem = route.Component;
                return (
                  <Route
                    key={route.path}
                    index={route.index}
                    path={route.path}
                    element={
                      React.isValidElement(Elem) ? (
                        <Suspense>{Elem}</Suspense>
                      ) : (
                        <Suspense>
                          <Elem />
                        </Suspense>
                      )
                    }
                  ></Route>
                );
              }
            })}
          </Route>
        )}
        {children}
        {allowedRoutes.routes?.length > 0 && (
          <Route path="*" element={<NotFoundRoute />} />
        )}
      </Routes>
    </RouteManagerContext.Provider>
  );
};
